import React from "react"
import Tooltip from "app/static/frontend/components/Tooltip"
import { Icon } from "quorumdesign"
import "app/static/frontend/components/stylus/ChevronTimeline.styl"

/**
 * Timeline step status types
 */
type StepStatus = "completed" | "current" | "pending" | "vetoed" | "final-success"

/**
 * Icon types for timeline steps
 */
type StepIcon = "check" | "x" | null

/**
 * Tooltip placement options
 */
type TooltipPlacement = "beforeDate" | "afterLabel"

/**
 * Timeline step object
 */
interface TimelineStep {
    id: string
    label: string
    date?: string
    status: StepStatus
    icon?: StepIcon
    effectiveDateSource?: number
    vetoType?: number
    vetoOverridden?: boolean
}

/**
 * ChevronTimeline component props
 */
interface ChevronTimelineProps {
    steps: TimelineStep[]
}

/**
 * ChevronTimeline Component
 *
 * Displays a legislative timeline with interlocking chevron (arrow) shapes.
 * Supports 5 visual states: completed, current, pending, vetoed, final-success.
 *
 * Features:
 * - Chevron shapes using CSS pseudo-elements
 * - Date display below each step (always visible)
 * - Icons for vetoed (X) and final-success (check) states
 * - Responsive wrapping with proper row gaps
 *
 * @param {ChevronTimelineProps} props
 * @param {TimelineStep[]} props.steps - Array of timeline step objects
 */
export const ChevronTimeline: React.FC<ChevronTimelineProps> = ({ steps }) => {
    if (!steps || steps.length === 0) {
        return null
    }

    /**
     * Format date from YYYY-MM-DD to MM/DD/YY
     * Parses date string manually to avoid timezone conversion issues.
     * When new Date("2024-03-15") is called, JS treats it as UTC midnight,
     * which can shift to the previous day in local timezone.
     */
    const formatDate = (dateString: string | undefined): string => {
        if (!dateString) return ""

        try {
            // Parse YYYY-MM-DD manually to avoid timezone issues
            const parts = dateString.split("-")
            if (parts.length !== 3) {
                console.warn(`[ChevronTimeline] Invalid date format: ${dateString}`)
                return dateString
            }
            const [year, month, day] = parts
            if (!year || !month || !day) {
                console.warn(`[ChevronTimeline] Missing date components: ${dateString}`)
                return dateString
            }
            const shortYear = year.slice(-2)
            return `${month}/${day}/${shortYear}`
        } catch (e) {
            console.warn(`[ChevronTimeline] Error parsing date: ${dateString}`, e)
            return dateString
        }
    }

    /**
     * Render icon for vetoed or final-success states
     */
    const renderIcon = (step: TimelineStep): JSX.Element | null => {
        if (step.icon === "x") {
            return <span className="chevron-icon chevron-icon-x">✕</span>
        }

        if (step.icon === "check") {
            return <span className="chevron-icon chevron-icon-check">✓</span>
        }

        return null
    }

    /**
     * Check if the effective date was assumed (not explicit)
     * EffectiveDateSource enum values:
     * 1 = explicit (stated in bill text)
     * 2 = assumed (calculated from state rules)
     * 3 = immediate (effective immediately)
     */
    const hasAssumedEffectiveDate = (step: TimelineStep): boolean => {
        return step.effectiveDateSource === 2
    }

    /**
     * Check if the veto should show a tooltip
     * VetoType enum values:
     * 1 = full (complete veto of entire bill)
     * 2 = partial (line-item veto, some sections vetoed)
     * 3 = pocket (pocket veto)
     *
     * Show tooltip for:
     * - Overridden vetoes (veto exists but bill reached Enacted/Effective status)
     * - Partial vetoes (some provisions vetoed, others became law)
     */
    const shouldShowVetoTooltip = (step: TimelineStep): boolean => {
        if (step.id !== "vetoed") {
            return false
        }

        // Show tooltip if veto was overridden
        if (step.vetoOverridden) {
            return true
        }

        // Show tooltip if it's a partial veto (type=2)
        if (step.vetoType === 2) {
            return true
        }

        return false
    }

    /**
     * Render tooltip icon for veto outcomes
     * Shows on:
     * - Vetoed step when veto was overridden - after the label text
     * - Vetoed step when it's a partial veto - after the label text
     *
     * Does NOT show for:
     * - Full vetoes that were NOT overridden (standard veto case)
     */
    const renderVetoTooltip = (step: TimelineStep, placement: TooltipPlacement = "afterLabel"): JSX.Element | null => {
        // Only show tooltip for vetoed steps
        if (!shouldShowVetoTooltip(step)) {
            return null
        }

        // Only render when placement is "afterLabel"
        if (placement !== "afterLabel") {
            return null
        }

        // Determine tooltip text based on veto type and override status
        let tooltipText = ""
        if (step.vetoOverridden && step.vetoType === 2) {
            tooltipText =
                "This was a partial veto that was overridden by the legislature. Some provisions were vetoed while others became law"
        } else if (step.vetoOverridden) {
            tooltipText = "This veto was overridden by the legislature, and the bill became law despite the veto"
        } else if (step.vetoType === 2) {
            tooltipText = "This was a partial veto. Some provisions were vetoed while others became law"
        }

        return (
            <Tooltip label={tooltipText} placement="top">
                <span
                    className="chevron-veto-icon"
                    role="img"
                    aria-label="Veto information"
                    style={{
                        marginLeft: "4px",
                        cursor: "help",
                        display: "inline-flex",
                        width: "12px",
                        height: "12px",
                        justifyContent: "center",
                        alignItems: "center",
                        flexShrink: 0,
                    }}
                >
                    <Icon icon="exclamation-triangle" iconFamily="fas" size="sm" color="#F86816" />
                </span>
            </Tooltip>
        )
    }

    /**
     * Render tooltip icon for assumed effective dates
     * Shows ONLY on:
     * - Effective step (when shown) - before the date
     *
     * Does NOT show on:
     * - Enacted step (removed per user request)
     */
    const renderAssumedDateTooltip = (
        step: TimelineStep,
        placement: TooltipPlacement = "beforeDate",
    ): JSX.Element | null => {
        // Only show tooltip for steps with assumed effective date source
        if (!hasAssumedEffectiveDate(step)) {
            return null
        }

        // Only show on Effective step (NOT on Enacted step)
        if (step.id !== "effective") {
            return null
        }

        // Only show before the date
        if (placement !== "beforeDate") {
            return null
        }

        const tooltipText =
            "This effective date was assumed based on legislative rules, as it was not explicitly stated in the bill text"

        const iconStyle: React.CSSProperties = {
            cursor: "help",
            display: "inline-flex",
            width: "12px",
            height: "12px",
            justifyContent: "center",
            alignItems: "center",
            flexShrink: 0,
            marginRight: "4px",
        }

        return (
            <Tooltip label={tooltipText} placement="top">
                <span className="chevron-assumed-icon" role="img" aria-label="Assumed effective date" style={iconStyle}>
                    <Icon icon="exclamation-triangle" iconFamily="fas" size="sm" color="#F86816" />
                </span>
            </Tooltip>
        )
    }

    return (
        <div className="chevron-timeline">
            <ol className="stat_leg">
                {steps.map((step) => (
                    <li key={step.id} className={step.status}>
                        <div className="timeline-text">
                            {renderIcon(step)}
                            {step.label}
                            {renderVetoTooltip(step, "afterLabel")}
                        </div>
                        {step.date && (
                            <div className="timeline-date">
                                {renderAssumedDateTooltip(step, "beforeDate")}
                                {formatDate(step.date)}
                            </div>
                        )}
                    </li>
                ))}
            </ol>
            <div style={{ clear: "both" }}></div>
        </div>
    )
}

export default ChevronTimeline
